if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.sectionId = this.dataset.sectionId;           // set in buy-buttons.liquid
        this.formId = this.form?.id;
        this.variantIdInput.disabled = false;

        // bind listeners to option controls tied to this form
        this.bindOptionListeners();

        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');
        this.hideErrors = this.dataset.hideErrors === 'true';

        // initial sync
        this.updateVariantId();

        // rebind if DOM changes (some themes re-render pickers)
        this._mo = new MutationObserver(() => this.bindOptionListeners());
        this._mo.observe(document.body, { subtree: true, childList: true });
      }

      // ---------- helpers ----------

      pickerRoot() {
        return document.querySelector(`#variant-selects-${this.sectionId}`) || document;
      }

      // normalize/trim problematic attributes at runtime
      sanitizeInputs(inputs) {
        inputs.forEach((el) => {
          if (typeof el.name === 'string') el.name = el.name.trim();
        });
      }

      // all inputs that control options for THIS product form
      getOptionInputs() {
        const scope = this.pickerRoot();
        const arr = [];

        // radios & selects tied to our form via [form="<id>"]
        arr.push(...scope.querySelectorAll(`input[type="radio"][form="${this.formId}"]`));
        arr.push(...scope.querySelectorAll(`select[form="${this.formId}"]`));

        // fallbacks: anything inside the form
        arr.push(...this.form.querySelectorAll('input[type="radio"], select'));

        // dedupe by element reference
        const seen = new Set();
        const unique = arr.filter((el) => {
          if (seen.has(el)) return false;
          seen.add(el);
          return true;
        });

        this.sanitizeInputs(unique);
        return unique;
      }

      bindOptionListeners() {
        // remove previous
        if (this._boundOptionInputs) {
          this._boundOptionInputs.forEach((el) => {
            el.removeEventListener('change', this._pfHandler);
            el.removeEventListener('click', this._pfHandler);
            el.removeEventListener('variant:changed', this._pfHandler);
          });
        }

        const inputs = this.getOptionInputs();
        this._pfHandler = () => this.updateVariantId();

        // bind directly on inputs
        inputs.forEach((el) => {
          el.addEventListener('change', this._pfHandler);
          el.addEventListener('click', this._pfHandler);
          el.addEventListener('variant:changed', this._pfHandler);
        });
        this._boundOptionInputs = inputs;

        // plus: delegate from the picker root as a safety net
        const root = this.pickerRoot();
        if (!this._delegated) {
          this._delegated = (e) => {
            const t = e.target;
            if (!t) return;
            if (
              (t.matches && (t.matches('input[type="radio"][form]') || t.matches('select[form]'))) ||
              t.closest?.('input[type="radio"][form], select[form]')
            ) {
              this.updateVariantId();
            }
          };
          root.addEventListener('change', this._delegated, true);
          root.addEventListener('click', this._delegated, true);
        }
      }

      // returns selected option values ordered as [option1, option2, option3]
      collectSelectedOptions() {
        const selected = [undefined, undefined, undefined];

        // radios: detect position from the ID suffix "-<pos>-<index>"
        const radios = this.getOptionInputs().filter((el) => el.type === 'radio');
        radios.forEach((el) => {
          if (!el.checked) return;
          const m = el.id && el.id.match(/-(\d)-\d+$/);
          const pos = m ? parseInt(m[1], 10) : NaN; // 1..3
          if (pos >= 1 && pos <= 3) selected[pos - 1] = (el.value ?? '').trim();
        });

        // selects: use canonical ids (#Option-<sectionId>-0/1/2) when present,
        // otherwise rely on DOM order within the picker root.
        for (let pos = 1; pos <= 3; pos++) {
          if (selected[pos - 1] !== undefined) continue; // set by a radio
          const byId =
            document.querySelector(`#Option-${this.sectionId}-${pos - 1}[form="${this.formId}"]`) ||
            this.form.querySelector(`#Option-${this.sectionId}-${pos - 1}`);
          if (byId) {
            selected[pos - 1] = (byId.value ?? '').trim();
            continue;
          }
          const selects = Array.from(this.pickerRoot().querySelectorAll(`select[form="${this.formId}"]`));
          if (selects[pos - 1]) selected[pos - 1] = (selects[pos - 1].value ?? '').trim();
        }

        // trim trailing empties
        while (selected.length && (selected[selected.length - 1] === undefined || selected[selected.length - 1] === '')) {
          selected.pop();
        }
        return selected;
      }

      // ---------- core: set hidden variant id ----------

      updateVariantId() {
        try {
          // prefer full product json, fallback to variants-only
          const productEl = this.querySelector('[data-product]');
          const variantsEl = this.querySelector('[data-product-variants]');
          if (!productEl && !variantsEl) return;

          const productData = productEl ? JSON.parse(productEl.textContent) : null;
          const variants = productData ? productData.variants : JSON.parse(variantsEl.textContent);

          const selected = this.collectSelectedOptions();
          const norm = (s) => (s == null ? '' : String(s).trim());

          // find matching variant (trim/normalize both sides)
          const match = variants.find((v) => {
            const opts = [v.option1, v.option2, v.option3].filter((o) => o !== null && o !== undefined);
            return opts.every((val, i) => norm(val) === norm(selected[i]));
          });

          if (match && this.variantIdInput) {
            this.variantIdInput.value = match.id; // critical fix
            this.variantIdInput.disabled = false;
            this.dispatchEvent(new CustomEvent('variant:change', { detail: { variant: match } }));
          }
        } catch (e) {
          console.warn('variant id update failed', e);
        }
      }

      // ---------- submit flow ----------

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // ensure id is fresh (last line of defence)
        this.updateVariantId();

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
